import React, { useState, useEffect, useMemo } from "react";
import {
    FiCalendar,
    FiCheckCircle,
    FiChevronDown,
    FiChevronLeft,
    FiChevronRight,
    FiChevronUp,
    FiFileText,
    FiHome,
    FiMessageSquare,
    FiSearch,
} from "react-icons/fi";
import { MdHelpOutline, MdSupportAgent, MdTaskAlt } from "react-icons/md";
import { getContactSupport, updateContactSupportStatus } from "../Services/contactSupportService"
import ConfirmationModal from "../Components/ConfirmationModal";
import { toast } from 'react-toastify';
import { contactSupportOptions, RoutesPaths } from "../Router/constant";
import Skeleton from "../Components/SkeletonLoader";
import { NavLink } from "react-router-dom";

export default function Contactsupport() {
    const [loading, setLoading] = useState(true);
    const [offset, setOffset] = useState(1);
    const [limit] = useState(10);
    const [status, setStatus] = useState("");
    const [confirmation, setConfirmation] = useState({
        show: false,
        contactId: null,
    });
    const [contactSupportData, setContactSupportData] = useState([]);
    const [count, setCount] = useState(0);
    const [closedCounts, setClosedCounts] = useState(0);
    const [openCounts, setOpenCounts] = useState(0);
    // Search and sort apply to the rows of the current page; the API only takes status.
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState({ key: null, dir: "asc" });

    const handleDeleteClick = (contactId) => {
        setConfirmation({ show: true, contactId });
    };
    const handleConfirmDelete = async () => {
        try {
            if (confirmation.contactId) {
                const response = await updateContactSupportStatus(confirmation.contactId);
                if (response.data?.status === true) {
                    toast.success('Contact Closed Successfully');
                    fetchSupportData();
                    setConfirmation({ show: false, contactId: null });
                } else {
                    console.error("Delete failed:", response.data?.message || "Unknown error");
                }
            }
        } catch (error) {
            console.error("Error deleting contact:", error);
        }
    };

    const handleCancelDelete = () => {
        setConfirmation({ show: false, contactId: null });
    };

    const fetchSupportData = async () => {
        setLoading(true);

        try {
            const contactResp = await getContactSupport({ offset, limit, status });
            const data = contactResp.data?.data?.contacts || [];
            setContactSupportData(data);
            setCount(contactResp?.data?.data?.total || 0);
            setOpenCounts(contactResp?.data?.data?.opened || 0);
            setClosedCounts(contactResp?.data?.data?.closed || 0)

        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        fetchSupportData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offset, status]);

    const rows = useMemo(() => {
        const query = search.trim().toLowerCase();
        let list = query
            ? contactSupportData.filter((c) =>
                [c.fullName, c.email, c.message, c.phoneNumber]
                    .filter(Boolean)
                    .some((v) => String(v).toLowerCase().includes(query)),
            )
            : contactSupportData;

        if (sort.key) {
            const value = (c) =>
                sort.key === "date" ? new Date(c.createdAt).getTime() : String(c.fullName || "").toLowerCase();
            list = [...list].sort((a, b) => {
                const diff = value(a) > value(b) ? 1 : value(a) < value(b) ? -1 : 0;
                return sort.dir === "asc" ? diff : -diff;
            });
        }
        return list;
    }, [contactSupportData, search, sort]);

    const toggleSort = (key) =>
        setSort((prev) => ({
            key,
            dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc",
        }));

    const SortArrows = ({ column }) => (
        <span className="qb-sort-arrows">
            <FiChevronUp className={sort.key === column && sort.dir === "asc" ? "on" : ""} />
            <FiChevronDown className={sort.key === column && sort.dir === "desc" ? "on" : ""} />
        </span>
    );

    const summaryCards = [
        { label: "Total tickets", value: count, tone: "orange", Icon: FiMessageSquare, Art: MdSupportAgent },
        { label: "Open tickets", value: openCounts, tone: "amber", Icon: FiFileText, Art: MdHelpOutline },
        { label: "Close tickets", value: closedCounts, tone: "green", Icon: FiCheckCircle, Art: MdTaskAlt },
    ];

    const totalPages = Math.ceil(count / limit);
    const firstRow = count === 0 ? 0 : (offset - 1) * limit + 1;
    const lastRow = Math.min(offset * limit, count);

    return (
        <div className="container-fluid qd-page">
            {/* Breadcrumb */}
            <nav className="qd-breadcrumb">
                <NavLink to={RoutesPaths.DASHBOARD} aria-label="Dashboard">
                    <FiHome />
                </NavLink>
                <FiChevronRight />
                <span className="qd-breadcrumb-current">Contact support</span>
            </nav>

            {/* Summary cards */}
            <div className="qd-summary-grid">
                {summaryCards.map(({ label, value, tone, Icon, Art }) => (
                    <div className={`qd-card qd-summary qd-summary-${tone} qc-summary`} key={label}>
                        <div className="qd-stat-body">
                            <span className={`qd-stat-icon qd-tone-${tone} qc-summary-icon`}>
                                <Icon />
                            </span>
                            {loading ? (
                                <>
                                    <div className="qd-skel" style={{ width: 50, height: 32, marginBottom: 6 }} />
                                    <div className="qd-skel" style={{ width: 100, height: 16 }} />
                                </>
                            ) : (
                                <>
                                    <h3 className="qb-summary-value">{value}</h3>
                                    <p className="qb-summary-label">{label}</p>
                                </>
                            )}
                        </div>
                        <span className="qb-summary-art" aria-hidden="true">
                            <Art />
                        </span>
                    </div>
                ))}
            </div>

            {/* Tickets table */}
            <div className="qd-card qd-panel">
                <div className="qd-panel-header">
                    <div className="qd-panel-title">
                        <span className="qd-panel-icon">
                            <FiMessageSquare />
                        </span>
                        <div>
                            <h6>Contact support details</h6>
                            <p className="qd-panel-sub">
                                Manage all customer support tickets and respond to their queries.
                            </p>
                        </div>
                    </div>
                    <div className="qd-bookings-tools">
                        <label className="qd-search qc-search">
                            <FiSearch />
                            <input
                                type="text"
                                placeholder="Search name, email or message..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </label>
                        <select
                            className="qd-select qd-select-wide"
                            value={status}
                            onChange={(e) => {
                                setStatus(e.target.value);
                                setOffset(1);
                            }}
                            aria-label="Filter by status"
                        >
                            {contactSupportOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="qd-table-wrap">
                    <table className="qd-table qc-table">
                        <thead>
                            <tr>
                                <th>
                                    <button type="button" className="qb-sort" onClick={() => toggleSort("name")}>
                                        Customer name <SortArrows column="name" />
                                    </button>
                                </th>
                                <th>Email</th>
                                <th>Phone number</th>
                                <th>Message</th>
                                <th>
                                    <button type="button" className="qb-sort" onClick={() => toggleSort("date")}>
                                        Date <SortArrows column="date" />
                                    </button>
                                </th>
                                <th>Status</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                [...Array(5)].map((_, i) => (
                                    <tr key={i}>
                                        <td><Skeleton width={120} /></td>
                                        <td><Skeleton width={150} /></td>
                                        <td><Skeleton width={100} /></td>
                                        <td><Skeleton width={140} /></td>
                                        <td><Skeleton width={90} /></td>
                                        <td><Skeleton width={70} height={24} /></td>
                                        <td><Skeleton width={90} height={32} /></td>
                                    </tr>
                                ))
                            ) : rows.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="qd-empty">No contact support found</td>
                                </tr>
                            ) : (
                                rows.map((contact) => {
                                    const isOpen = contact.status === "opened";
                                    return (
                                        <tr key={contact._id}>
                                            <td>
                                                <span className="qd-customer">
                                                    <span className="qd-avatar">
                                                        {(contact.fullName || "?").trim().charAt(0).toUpperCase()}
                                                    </span>
                                                    <strong>{contact.fullName}</strong>
                                                </span>
                                            </td>
                                            <td>{contact.email}</td>
                                            <td>{contact.phoneNumber}</td>
                                            <td title={contact.message}>
                                                <span className="qc-message">{contact.message}</span>
                                            </td>
                                            <td>
                                                <span className="qd-date">
                                                    <FiCalendar />
                                                    {new Date(contact.createdAt).toLocaleDateString("en-GB")}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`qc-status ${isOpen ? "open" : "closed"}`}>
                                                    <span className="qu-dot" />
                                                    {isOpen ? "Open" : "Closed"}
                                                </span>
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="qd-btn qd-btn-primary qd-btn-sm qc-close-btn"
                                                    onClick={() => handleDeleteClick(contact._id)}
                                                    disabled={!isOpen}
                                                    title={isOpen ? "Close this ticket" : "Ticket already closed"}
                                                >
                                                    <FiCheckCircle /> Close
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {!loading && count > 0 && (
                    <div className="qd-pagination">
                        <span className="qd-muted">
                            Showing {firstRow} to {lastRow} of {count} tickets
                        </span>
                        <div className="qd-pages">
                            <button disabled={offset <= 1} onClick={() => setOffset(offset - 1)} aria-label="Previous">
                                <FiChevronLeft />
                            </button>
                            {[...Array(totalPages)].map((_, index) => (
                                <button
                                    key={index}
                                    className={offset === index + 1 ? "active" : ""}
                                    onClick={() => setOffset(index + 1)}
                                >
                                    {index + 1}
                                </button>
                            ))}
                            <button disabled={offset >= totalPages} onClick={() => setOffset(offset + 1)} aria-label="Next">
                                <FiChevronRight />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <ConfirmationModal
                show={confirmation.show}
                title="Confirm close"
                message="Do you really want to close this ticket?"
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                confirmText="Yes, close"
                cancelText="Cancel"
            />
        </div>
    );
}
